import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import type { FaqCategory } from '../../types/faq';

type Props = {
  category: FaqCategory;
};

/**
 * Prépare une réponse pour l'affichage : le texte est échappé avant d'ajouter
 * la mise en gras (**texte**). Sans cet échappement, une réponse saisie dans le
 * back office contenant un chevron (« bébé de < 3 mois ») verrait une partie de
 * son texte disparaître, interprétée comme une balise HTML.
 */
const renderAnswer = (answer: string): string =>
  answer
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

const AccordionItem: React.FC<{ q: string; a: string; isOpen: boolean; onClick: () => void }> = ({ q, a, isOpen, onClick }) => {
  return (
    <div className="border-b border-neutral-line py-6">
      <button
        onClick={onClick}
        className="w-full flex justify-between items-start text-left gap-4"
        aria-expanded={isOpen}
      >
        <h3 className="font-playfair font-semibold text-neutral-ink text-xl flex-1">{q}</h3>
        <motion.div
          className="mt-1 text-accent-sage"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <ChevronDown size={24} />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0, marginTop: 0 }}
            animate={{ height: 'auto', opacity: 1, marginTop: '1rem' }}
            exit={{ height: 0, opacity: 0, marginTop: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div 
              className="font-nunito text-neutral-body whitespace-pre-wrap pr-8"
              dangerouslySetInnerHTML={{ __html: renderAnswer(a) }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const FaqSection: React.FC<Props> = ({ category }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleItem = (index: number) => {
    setOpenIndex(prev => (prev === index ? null : index));
  };

  return (
    <section id={category.slug} className="mb-16">
      <h2 className="font-playfair text-3xl md:text-4xl font-bold text-neutral-ink mb-4 text-center">
        {category.title}
      </h2>
      
      <div>
        {category.items.map((item, idx) => (
          <AccordionItem
            key={item.id}
            q={item.question}
            a={item.answer}
            isOpen={openIndex === idx}
            onClick={() => toggleItem(idx)}
          />
        ))}
      </div>
    </section>
  );
};

export default FaqSection;
